import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import {parse} from 'yaml';

import {Ace, edit} from 'ace-builds';

import 'ace-builds';
import 'ace-builds/src-noconflict/theme-twilight';
import 'ace-builds/src-noconflict/theme-clouds_midnight';
import 'ace-builds/src-noconflict/theme-pastel_on_dark';
import 'ace-builds/src-noconflict/theme-crimson_editor';
import 'ace-builds/src-noconflict/theme-gob';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-yaml';
import {AbstractControl, NG_VALIDATORS, ValidationErrors, Validator} from '@angular/forms';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Utils} from '../../../core/helpers/Utils';
import { Range } from 'ace-builds';

@Component({
    selector: 'app-editor',
    templateUrl: './json-editor.component.html',
    styleUrls: ['./json-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => JsonEditorComponent),
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: JsonEditorComponent,
        },
    ],
    standalone: false
})
export class JsonEditorComponent implements ControlValueAccessor, Validator, AfterViewInit, OnChanges {
    get text() {
        return this._text;
    }

	@Input()
    set text(text: string) {
        this.setText(text);
    }

	_text!: string;

	@ViewChild('editor') editorRef!: ElementRef;
	@Output() textChange = new EventEmitter<string>();
	@Input() valid = false;
	@Output() validChange = new EventEmitter<boolean>();
	@Output() onEditorInit = new EventEmitter<any>();
	@Input() readOnly = false;
	@Input() mode = 'yaml';
	@Input() prettify = true;
	@Input() theme: string;
	@Input() errorMsg: string;
	@Input() class: string;

	@Output() documentCountChange = new EventEmitter<number>();
	public documentCount = 1;
	private docMarkers: number[] = [];

	touched = false;
	editor!: Ace.Editor;
	// All possible options can be found at:
	// https://github.com/ajaxorg/ace/wiki/Configuring-Ace
	aceOptions = {
	    showPrintMargin: false,
	    highlightActiveLine: true,
	    tabSize: 2,
	    wrap: true,
	    fontSize: 14,
	    fontFamily: "'Roboto Mono Regular', monospace",
	};

	onChange = (text: any) => {
	};
	onTouched = () => {
	};

	constructor(private _cdRef: ChangeDetectorRef) {

	}

	registerOnChange(onChange: any) {
	    this.onChange = onChange;
	}

	registerOnTouched(onTouched: any) {
	    this.onTouched = onTouched;
	}

	setDisabledState(isDisabled: boolean): void {
	    this.readOnly = isDisabled;
	}

	writeValue(text: any) {
	    this.setText(text instanceof Object ? Utils.convertToYamlFile(text) : text);
	}

	// @ts-ignore
	validate(control: AbstractControl): ValidationErrors | null {
	    const yamlText = control.value;
	    let error = this.getYamlCodeValidationErrors(yamlText, false);
	    if (error) {
	        return {
	            invalidYaml: {
	                yamlText,
	            },
	        };
	    }
	}

	ngAfterViewInit(): void {
	    this.initEditor_();
	}

	markAsTouched() {
	    if (!this.touched) {
	        this.onTouched();
	        this.touched = true;
	    }
	}

	setCollapse() {
	    this.editor.getSession().foldAll();
	}

	setExpand() {
	    const session = this.editor.getSession();
	    const length = session.getLength();
	    for (let i = 0; i < length; i++) {
	        session.unfold(i);
	    }
	}

	ngOnChanges(changes: SimpleChanges): void {
	    if (!this.editor) {
	        return;
	    }

	    for (const propName in changes) {
	        if (changes.hasOwnProperty(propName)) {
	            switch (propName) {
	            case 'mode':
	                this.onEditorModeChange_();
	                break;
	            case 'theme':
	                this.editor.setTheme(changes['theme'].currentValue);
	                break;
	            default:
	            }
	        }
	    }
	}

	private initEditor_(): void {
	    this.editor = edit(this.editorRef.nativeElement);
	    this.editor.setOptions(this.aceOptions);
	    this.editor.setValue(this.text || '', -1);
	    this.editor.setReadOnly(this.readOnly);
	    this.editor.setTheme(this.theme);
	    this.setEditorMode_();
	    this.editor.session.setUseWorker(false);
	    this.editor.on('change', () => this.onEditorTextChange_());
	    this.editor.focus();
	    this.editor.navigateFileEnd();
	    this.editor.commands.removeCommand('find');
	    this.onEditorInit.emit(this.editor);
	}

	private onEditorTextChange_(): void {
	    if (!this.editor)  {
	        return;
	    }
	    this._text = this.editor.getValue();
	    this.markAsTouched();
	    this.textChange.emit(this.text);
	    this.onChange(this.text);
	    let error = this.getYamlCodeValidationErrors(this.text, true);
	    this.validChange.emit(!error);
	    if (this.mode === 'yaml') {
	        this.setupMultiDocumentSupport();
	        setTimeout(this.adjustAnnotations.bind(this, error), 1);
	    }
	}

	public onEditorTextChange() {
	    this.onEditorTextChange_();
	}

	public setupMultiDocumentSupport(): void {
	    if (!this.editor) return;
	    this.removeDocumentMarkers();

	    const session = this.editor.getSession();
	    const lines = session.getDocument().getAllLines();
	    let docCount = 1;

	    lines.forEach((line, index) => {
	        if (line.trim() === '---') {
	            docCount++;
	            const markerId = session.addMarker(
	                new Range(index, 0, index, line.length),
	                "yaml-document-separator",
	                "fullLine",
	                false
	            );
	            this.docMarkers.push(markerId);
	        }
	    });
	    this.documentCount = docCount;
	    this.documentCountChange.emit(this.documentCount);
	}

	private removeDocumentMarkers(): void {
	    if (!this.editor) return;

	    const session = this.editor.getSession();
	    this.docMarkers.forEach(id => session.removeMarker(id));
	    this.docMarkers = [];
	}

	private adjustAnnotations(error: any) {
	    if (error && error.message) {
	        const session = this.editor.getSession();
	        let allLines = session.doc.getAllLines();

	        if (error.documentIndex !== undefined) {
	            let documentStartLine = 0;
	            let currentDocIndex = 0;

	            for (let i = 0; i < allLines.length; i++) {
	                if (allLines[i].trim() === '---') {
	                    currentDocIndex++;
	                    if (currentDocIndex === error.documentIndex) {
	                        documentStartLine = i + 1;
	                    }
	                }
	            }

	            let line = documentStartLine;
	            if (error.linePos?.length > 0) {
	                line = documentStartLine + error.linePos[0].line - 1;
	            }

	            session.setAnnotations([
	                {
	                    row: line,
	                    column: error.linePos?.length > 0 ? error.linePos[0].col : 1,
	                    text: `Document ${error.documentIndex + 1}: ${error.message}`,
	                    type: 'error',
	                },
	            ]);
	        } else {
	            let line = error.linePos?.length > 0 ? error.linePos[0].line - 1 : allLines.length - 1;
	            session.setAnnotations([
	                {
	                    row: line,
	                    column: error.linePos?.length > 0 ? error.linePos[0].col : 1,
	                    text: error.message,
	                    type: 'error',
	                },
	            ]);
	        }
	    } else {
	        this.editor.getSession().setAnnotations([]);
	    }
	}

	private onEditorModeChange_(): void {
	    this.setEditorMode_();
	}

	private setEditorMode_(): void {
	    this.editor.getSession().setMode(`ace/mode/${this.mode}`);
	}

	getYamlCodeValidationErrors(code: any, required: boolean) {
	    if (!code) {
	        return required;
	    } else {
	        let error: any;
	        try {
	            if (typeof code === 'string' && code.includes('---')) {
	                const documents = code.split(/^---$/m)
	                    .filter(doc => doc.trim());

	                for (let i = 0; i < documents.length; i++) {
	                    try {
	                        parse(documents[i]);
	                    } catch (e: any) {
	                        e.documentIndex = i;
	                        throw e;
	                    }
	                }
	                return null;
	            } else {
	                parse(code);
	            }
	        } catch (e) {
	            error = e;
	        }
	        return error;
	    }
	}

	setText(text: any) {
	    if (text === null || text === undefined) {
	        text = "";
	    }
	    if (this._text !== text) {
	        this._text = text;
	        this.onChange(text);
	        this.editor?.setValue(text);
	        this.editor?.clearSelection();
	    }

	    setTimeout(() => {
	        if (this.mode === 'yaml') {
	            this.setupMultiDocumentSupport();
	        }
	    }, 0);
	}
}
